import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Code2, KeyRound, Plus, Loader2, Copy, Ban, BookOpen } from "lucide-react";
import { PortalShell } from "../components/layout/PortalShell";
import { apiCreateKey, apiListKeys, apiRevokeKey, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENDPOINTS = [
  { method: "POST", path: "/ai/audit-prep", desc: "Generate an audit preparation report" },
  { method: "POST", path: "/ai/gap-analysis", desc: "Generate a compliance gap analysis" },
  { method: "POST", path: "/ai/document-generator", desc: "Draft a manual, SOP or policy" },
  { method: "POST", path: "/ai/corrective-action", desc: "Build a corrective action plan (CAPA)" },
  { method: "POST", path: "/ai/risk-scoring", desc: "Score risk & define SPIs" },
  { method: "POST", path: "/ai/training-competency", desc: "Build a training & competency program" },
  { method: "POST", path: "/ai/safety-analyzer", desc: "Analyze a safety report" },
  { method: "GET", path: "/ai/{module}", desc: "List your saved reports for any module above" },
  { method: "GET", path: "/auth/me", desc: "Check the authenticated account" },
  { method: "GET", path: "/billing/me", desc: "Check plan & remaining monthly quota" },
];

const CURL_EXAMPLE = `curl -X POST ${API_BASE}/ai/risk-scoring \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "organization_name": "SkyLink Charter",
    "organization_type": "Charter Operator",
    "regulatory_framework": "FAA Part 5 SMS",
    "audit_type": "Risk scoring & SPI assessment",
    "current_state": "Describe operations, hazards and controls..."
  }'`;

export default function Developers() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [freshKey, setFreshKey] = useState(null);

  const load = () => apiListKeys().then(setKeys).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const k = await apiCreateKey(name || "Default key");
      setFreshKey(k);
      setName("");
      toast.success("API key created — copy it now, it won't be shown again.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create key.");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (k) => {
    try {
      await apiRevokeKey(k.key_id);
      toast.success(`Key "${k.name}" revoked.`);
      load();
    } catch {
      toast.error("Could not revoke key.");
    }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard."); }
    catch { toast.error("Copy failed — select and copy manually."); }
  };

  return (
    <PortalShell>
      <div data-testid="developers-page">
        <div className="mb-8">
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-navy">Developer API</h1>
          <p className="mt-1 text-muted-foreground">Call every AuditGuard AI module from your own systems with an API key.</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-12">
          {/* Keys */}
          <div className="space-y-6 lg:col-span-5">
            <div className="rounded-2xl border border-border bg-white p-6">
              <div className="mb-4 flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-brand" />
                <h2 className="font-display text-lg font-bold text-navy">API keys</h2>
              </div>
              <form onSubmit={create} data-testid="create-key-form" className="flex items-end gap-3">
                <div className="flex-1">
                  <Label>Key name</Label>
                  <Input data-testid="key-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-2" placeholder="e.g. Operations dashboard" />
                </div>
                <button type="submit" disabled={creating} data-testid="create-key-submit" className="inline-flex items-center gap-1.5 rounded-lg bg-aero px-4 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create
                </button>
              </form>

              {freshKey && (
                <div data-testid="fresh-key-box" className="mt-5 rounded-xl border border-amber-300 bg-amber-50 p-4">
                  <div className="text-xs font-bold uppercase tracking-wide text-amber-700">Your new key — shown once</div>
                  <div className="mt-2 flex items-center gap-2">
                    <code data-testid="fresh-key-value" className="flex-1 break-all rounded-lg bg-white px-3 py-2 text-xs text-navy">{freshKey.api_key}</code>
                    <button onClick={() => copy(freshKey.api_key)} data-testid="copy-fresh-key" className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-white text-steel hover:text-brand" title="Copy">
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-amber-700">Store it somewhere safe. If you lose it, revoke and create a new one.</p>
                </div>
              )}

              <ul className="mt-5 space-y-3">
                {keys.map((k) => (
                  <li key={k.key_id} data-testid={`key-${k.key_id}`} className="flex items-center justify-between gap-3 rounded-xl border border-border px-4 py-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-navy">{k.name}</div>
                      <div className="text-xs text-muted-foreground">
                        <code>{k.prefix}…</code> · created {new Date(k.created_at).toLocaleDateString()}
                        {k.last_used_at ? ` · last used ${new Date(k.last_used_at).toLocaleDateString()}` : " · never used"}
                      </div>
                    </div>
                    {k.revoked ? (
                      <span className="shrink-0 rounded-full bg-red-100 px-2.5 py-1 text-[11px] font-semibold uppercase text-red-600">Revoked</span>
                    ) : (
                      <button onClick={() => revoke(k)} data-testid={`revoke-${k.key_id}`} className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-steel hover:text-destructive">
                        <Ban className="h-3.5 w-3.5" /> Revoke
                      </button>
                    )}
                  </li>
                ))}
                {keys.length === 0 && <li className="py-4 text-center text-sm text-muted-foreground">No keys yet — create your first one above.</li>}
              </ul>
            </div>
          </div>

          {/* Docs */}
          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-border bg-white p-6">
              <div className="mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-brand" />
                <h2 className="font-display text-lg font-bold text-navy">API documentation</h2>
              </div>
              <div className="space-y-2 text-sm">
                <p className="text-muted-foreground">Base URL: <code className="rounded bg-secondary px-2 py-0.5 text-xs text-navy">{API_BASE}</code></p>
                <p className="text-muted-foreground">Authenticate every request with header: <code className="rounded bg-secondary px-2 py-0.5 text-xs text-navy">X-API-Key: YOUR_API_KEY</code></p>
                <p className="text-muted-foreground">Generations count against your plan's monthly quota, exactly like in-app use.</p>
              </div>
              <div className="mt-5 overflow-x-auto rounded-xl border border-border">
                <table className="w-full text-sm" data-testid="api-endpoints-table">
                  <thead className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr><th className="p-3">Method</th><th className="p-3">Endpoint</th><th className="p-3">What it does</th></tr>
                  </thead>
                  <tbody>
                    {ENDPOINTS.map((e) => (
                      <tr key={e.method + e.path} className="border-b border-border/60">
                        <td className="p-3"><span className={`rounded px-2 py-0.5 text-xs font-bold ${e.method === "POST" ? "bg-emerald-100 text-emerald-700" : "bg-brand/10 text-brand"}`}>{e.method}</span></td>
                        <td className="p-3"><code className="text-xs text-navy">{e.path}</code></td>
                        <td className="p-3 text-muted-foreground">{e.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <h3 className="mt-6 text-sm font-semibold text-navy">Example request</h3>
              <div className="relative mt-2">
                <pre className="overflow-x-auto rounded-xl bg-navy p-4 text-xs leading-relaxed text-white/90">{CURL_EXAMPLE}</pre>
                <button onClick={() => copy(CURL_EXAMPLE)} data-testid="copy-curl" className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 text-white/80 hover:bg-white/20" title="Copy">
                  <Copy className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                All POST bodies share the same fields: organization_name, organization_type, regulatory_framework,
                audit_type, current_state, and optional focus_areas. Responses include the generated markdown report.
              </p>
            </div>
            <div className="mt-6 flex items-start gap-3 rounded-2xl border border-border bg-white p-5">
              <Code2 className="mt-0.5 h-5 w-5 shrink-0 text-aero" />
              <p className="text-sm text-muted-foreground">
                Need higher limits, a sandbox key for a customer, or help integrating? Contact the Global-Elect team
                through the website's quote form and mention <span className="font-medium text-navy">AuditGuard API</span>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </PortalShell>
  );
}
